import React, { createContext, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { ButtonContext } from '../button/Button';
import Dialog from '../dialog/Dialog';
import Icon from '../icon/Icon';
import IconButton from '../icon_button/IconButton';
import Tag, { TagVariant } from '../tag/Tag';

const styles = require('./SectionEdition.module.scss');

const toDialogContext = (context: ButtonContext): 'talent' | 'company' | 'neutral' =>
  (context === 'semantic' ? 'neutral' : context);

interface SlotContextValue {
  buttonContext: ButtonContext;
  defaultDeleteConfirmation?: DeleteConfirmation;
  defaultLabel?: string;
}
const SlotContext = createContext<SlotContextValue>({ buttonContext: 'neutral' });

export interface SectionEditionTag {
  children: React.ReactNode;
  icon?: string;
  variant: TagVariant;
}

export type SectionEditionStatus = 'complete' | 'missing' | 'mandatory';
export type SectionAction = (() => void) | boolean;

const STATUS_TAGS: Record<Exclude<SectionEditionStatus, 'complete'>, SectionEditionTag> = {
  missing:   { variant: 'warning', icon: 'circleAlert',   children: 'Missing'   },
  mandatory: { variant: 'error',   icon: 'triangleAlert', children: 'Mandatory' },
};

export interface SectionEditionProps {
  allowMultiple?: boolean;
  children?: React.ReactNode;
  className?: string;
  context?: ButtonContext;
  defaultDeleteConfirmation?: DeleteConfirmation;
  emptyMessage?: string;
  iconName?: string;
  onAdd?: SectionAction;
  onClose?: () => void;
  onEdit?: SectionAction;
  status?: SectionEditionStatus;
  tag?: SectionEditionTag;
  title?: string;
  trailingText?: string;
}

const SectionEdition: React.FC<SectionEditionProps> = ({
  allowMultiple = false,
  children,
  className,
  context = 'neutral',
  defaultDeleteConfirmation,
  emptyMessage,
  iconName,
  onAdd,
  onClose,
  onEdit,
  status,
  tag,
  title,
  trailingText,
}) => {
  const resolvedTag = tag ?? (status && status !== 'complete' ? STATUS_TAGS[status] : undefined);
  const isEmpty = React.Children.toArray(children).length === 0;

  const slotContextValue = useMemo<SlotContextValue>(
    () => ({
      buttonContext: context,
      defaultDeleteConfirmation,
      defaultLabel: title?.toLowerCase(),
    }),
    [context, defaultDeleteConfirmation, title],
  );

  const hasHeader = Boolean(title || iconName || resolvedTag || onAdd || onEdit || trailingText || onClose);

  return (
    <SlotContext.Provider value={slotContextValue}>
      <section className={classNames(styles.container, className)} aria-label={title}>
        {hasHeader && (
          <div className={styles.header}>
            <div className={styles.titleGroup}>
              {iconName && (
                <div className={styles.iconWrapper}>
                  <Icon name={iconName} color="neutral-500" size="md" aria-hidden="true" />
                </div>
              )}
              {title && <h3 className={styles.title}>{title}</h3>}
            </div>
            {resolvedTag && (
              <Tag variant={resolvedTag.variant} iconLeft={resolvedTag.icon}>
                {resolvedTag.children}
              </Tag>
            )}
            {onAdd && (allowMultiple || !children) && (
              <IconButton
                type="terceary"
                size="S"
                context={context}
                iconName="plus"
                onClick={typeof onAdd === 'function' ? onAdd : undefined}
                aria-label={`Add ${title?.toLowerCase() ?? ''}`.trim()}
              />
            )}
            {onEdit && !allowMultiple && children && (
              <IconButton
                type="terceary"
                size="S"
                context={context}
                iconName="pencil"
                onClick={typeof onEdit === 'function' ? onEdit : undefined}
                aria-label={`Edit ${title?.toLowerCase() ?? ''}`.trim()}
              />
            )}
            {trailingText && (
              <span className={styles.trailingText}>{trailingText}</span>
            )}
            {onClose && (
              <IconButton
                type="terceary"
                size="S"
                context={context}
                iconName="x"
                onClick={onClose}
                aria-label="Close"
              />
            )}
          </div>
        )}
        {isEmpty && emptyMessage
          ? <Slot><p className={styles.emptyMessage}>{emptyMessage}</p></Slot>
          : children}
      </section>
    </SlotContext.Provider>
  );
};

export default SectionEdition;

export interface DeleteConfirmation {
  cancelText?: string;
  confirmText?: string;
  message?: React.ReactNode;
  title: string;
}

export interface SlotProps {
  children: React.ReactNode;
  context?: ButtonContext;
  deleteConfirmation?: DeleteConfirmation;
  label?: string;
  onDelete?: () => void;
  onEdit?: () => void;
  side?: boolean;
  stretch?: boolean;
}

export const Slot: React.FC<SlotProps> = ({
  children,
  context,
  deleteConfirmation,
  label,
  onDelete,
  onEdit,
  side = false,
  stretch = false,
}) => {
  const ctx = useContext(SlotContext);
  const resolvedContext = context ?? ctx.buttonContext;
  const resolvedLabel = label ?? ctx.defaultLabel;
  const resolvedDeleteConfirmation = deleteConfirmation ?? ctx.defaultDeleteConfirmation;

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const slotClass = side ? styles.sideSlot : stretch ? styles.stretchSlot : styles.slot;

  const handleDeleteClick = () => {
    if (!onDelete) return;
    if (resolvedDeleteConfirmation) {
      setIsConfirmOpen(true);
    } else {
      onDelete();
    }
  };

  const handleConfirm = () => {
    setIsConfirmOpen(false);
    onDelete?.();
  };

  return (
    <div className={slotClass}>
      <div className={styles.slotContent}>{children}</div>
      {onEdit || onDelete ? 
        <div className={styles.slotActions}>
          {onEdit && (
            <IconButton
              type="terceary"
              size="S"
              context={resolvedContext}
              iconName="pencil"
              onClick={onEdit}
              aria-label={resolvedLabel ? `Edit ${resolvedLabel}` : 'Edit'}
            />
          )}
          {onDelete && (
            <IconButton
              type="terceary"
              size="S"
              context={resolvedContext}
              iconName="trash"
              onClick={handleDeleteClick}
              aria-label={resolvedLabel ? `Delete ${resolvedLabel}` : 'Delete'}
            />
          )}
        </div> : null
      }
      {resolvedDeleteConfirmation && (
        <Dialog
          isOpen={isConfirmOpen}
          title={resolvedDeleteConfirmation.title}
          message={resolvedDeleteConfirmation.message ?? ''}
          cancelText={resolvedDeleteConfirmation.cancelText ?? 'Cancel'}
          confirmText={resolvedDeleteConfirmation.confirmText ?? 'Delete'}
          context={toDialogContext(resolvedContext)}
          onClose={() => setIsConfirmOpen(false)}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
};
